// Utility functions

import { format } from "date-fns"
import _ from "lodash"
import {
  AirportSeasonConfiguration,
  AirportSeasonConfigurations
} from "../api/AirportSeasonConfiguration"
import { Flight } from "../api/Flight"
import { handleGetTransactionPageGroup, PageId } from "../common/pageId"

import { ActionTypeText, flightModeText } from "../common/text"
import { ActionType, PageGroup } from "../common/transaction"
import { CodeType } from "./AirportSelector"

// returns the YYYY-MM-DD representation of the Date
export function formatDate(date: Date): string {
  if (date == null) {
    return null
  }

  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()

  return (
    year +
    "-" +
    (month < 10 ? "0" + month : month.toString()) +
    "-" +
    (day < 10 ? "0" + day : day.toString())
  )
}

const months = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC"
]

export function convertDateToUTC(date: Date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  )
}

//Returns date from now
export const subtractYears = (years: number) => {
  const date = convertDateToUTC(new Date())
  const pastYears = date.getFullYear() - years
  date.setFullYear(pastYears)
  return date
}

// // returns date for 01MAR, '2021-03-01' and date
// export function dateFromString(dateStr: string): Date {
//   if (dateStr == null || dateStr.length === 0) {
//     return null
//   }
//   let date = new Date(dateStr)
//   if (typeof dateStr === "string" && dateStr.length === 5) {
//     const month = months.indexOf(dateStr.slice(2, dateStr.length)) + 1
//     if (month !== 0) {
//       const day = dateStr.slice(0, 2)
//       const year = new Date().getFullYear()
//       date = new Date(
//         year + "-" + month.toString().padStart(2, "0") + "-" + day
//       )
//     }
//   }
//   return date
// }

// returns 01MAR for 2021/03/01
export function formatDateShort(date: Date, readOnly?: boolean): string {
  if (date == null) {
    return null
  }

  let utcTime = convertDateToUTC(new Date(date))
  readOnly ? (utcTime = new Date(date)) : convertDateToUTC(new Date(date))
  const month = utcTime.getMonth()
  const day = utcTime.getDate()

  return (day < 10 ? "0" + day : day.toString()) + months[month]
}

// returns 01MAR2021 for 2021/03/01
export function formatDateShortwYear(date: Date, readOnly?: boolean): string {
  if (date == null) {
    return null
  }
  let utcTime = convertDateToUTC(new Date(date))
  readOnly ? (utcTime = new Date(date)) : convertDateToUTC(new Date(date))
  const year = utcTime.getFullYear()
  const month = utcTime.getMonth()
  const day = utcTime.getDate()

  return (day < 10 ? "0" + day : day.toString()) + months[month] + year
}

export const formatStringDate = (dateStr: string) => {
  const formattedDate = dateStr.substring(0, 2) + " " + dateStr.substring(2, 6)

  return formattedDate
}

export const formatISOStringDate = (date: Date) => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss")
}

export const getDateFromDateTimeString = (dateStr: string, timeStr: string) => {
  const date = new Date(dateStr)
  const h = timeStr.substring(0, 2)
  const m = timeStr.substring(2)
  date.setHours(Number(h))
  date.setMinutes(Number(m))

  return date
}

export function GetValidDateInSeason(
  seasonFromDateStr: string,
  seasonToDateStr: string,
  shortDateStr: string
): Date {
  const seasonFromDt: Date = new Date(seasonFromDateStr)
  const seasonToDt: Date = new Date(seasonToDateStr)

  let dt: Date
  try {
    const yearNum: number = seasonToDt.getFullYear()
    const monthNum: number = months.indexOf(shortDateStr.substring(2, 5)) // zero based - so 05JAN -> 0
    const dayNum: number = parseInt(shortDateStr.substring(0, 2))

    dt = new Date(yearNum, monthNum, dayNum) // the month is 0-indexed
  } catch (FormatException) {
    throw new Error("Invalid date or format: " + shortDateStr)
  }

  if (dt > seasonToDt) {
    dt.setFullYear(dt.getFullYear() - 1)
  }

  if (dt < seasonFromDt || dt > seasonToDt) {
    // throw new Error("Date " + shortDateStr + " not in season")
    dt = null
  }

  return dt
}

export function getTimeAndCodeFilteredAirportSeasons(
  useLocalTime: boolean,
  useICAOCode: boolean,
  airportSeasons: AirportSeasonConfigurations
): AirportSeasonConfigurations {
  return airportSeasons.filter(
    (as) =>
      (useLocalTime ? as.supportsLt : as.supportsUtc) &&
      (useICAOCode ? as.supportsIcao : as.supportsIata)
  )
}

export function getFlightSelectorConfiguration(
  airportSeasons: AirportSeasonConfigurations,
  season: string,
  airports: Array<string>,
  codeType: CodeType
): AirportSeasonConfiguration {
  const airportSeasonsClone = _.cloneDeep(airportSeasons)
  const configs: AirportSeasonConfigurations = _.filter(
    airportSeasonsClone,
    (rec) => {
      return (
        rec.season === season &&
        _.findIndex(airports, (a) => {
          return a === rec.airport
        }) !== -1 &&
        (codeType === CodeType.ICAO ? rec.supportsIcao : rec.supportsIata)
      )
    }
  )

  if (configs.length > 1) {
    let actualConfig: AirportSeasonConfiguration = _.cloneDeep(configs[0])

    actualConfig.operatorRestricted = configs.find((c) => c.operatorRestricted)
      ? true
      : false

    actualConfig.operators = _.chain(configs)
      .reduce((result, item) => {
        return item.operators[0] === ""
          ? result
          : result[0] === ""
          ? item.operators
          : _.intersection(result, item.operators)
      }, configs[0].operators)
      .uniq()
      .sort()
      .value()

    actualConfig.serviceTypes = _.chain(configs)
      .reduce((result, item) => {
        return _.intersection(result, item.serviceTypes)
      }, [])
      .uniq()
      .sort()
      .value()

    actualConfig.terminals = _.chain(configs)
      .reduce((result, item) => {
        return _.intersection(result, item.terminals)
      }, configs[0].terminals)
      .uniq()
      .sort()
      .value()

    return actualConfig
  } else if (configs.length === 1) {
    return configs[0]
  } else {
    return null
  }
}

export function getEmptyFlight(pageId?: PageId) {
  const transactionPageGroup = handleGetTransactionPageGroup(pageId)
  const isHandlingPage = transactionPageGroup === PageGroup.HANDLING
  return {
    flightId: 0,
    airport: "",
    operatorArr: "",
    serviceNoArr: "",
    operatorDep: "",
    serviceNoDep: "",
    startDate: "",
    endDate: "",
    requiredDateArr: "",
    requiredDateDep: "",
    doop: "",
    numSeats: "",
    aircraftType: "",
    originStation: "",
    lastStation: "",
    clearedTimeArr: "",
    clearedTimeDep: "",
    turnaroundDays: null,
    nextStation: "",
    destinationStation: "",
    serviceTypeArr: "",
    serviceTypeDep: "",
    woop: null,
    aircraftRegistration: "",
    requiredTimeArr: null,
    requiredTimeDep: null,
    reasonCodeArr: null,
    reasonCodeDep: null,
    minGroundTime: !isHandlingPage ? 0 : null,
    offerTimeArrFrom: null,
    offerTimeArrTo: null,
    offerTimeDepFrom: null,
    offerTimeDepTo: null,
    permissionCode: "",
    terminalArr: null,
    terminalDep: null,
    requestCode: "N",
    responseCode: null,
    slotIdArr: null,
    slotIdDep: null,
    noOfOperations: 0,
    selectionStatus: null,
    startsOutsideSelection: false,
    flightMode: "",
    transLineNum: 0,
    statusCode: 0,
    replyOptions: [],
    replyCode: null,
    // ground handling
    handler: "",
    vfr: !isHandlingPage ? false : null,
    towbar: !isHandlingPage ? false : null,
    mtow: !isHandlingPage ? 0 : null,
    gabaLounge: false,
    refuelling: false,
    operatorText: "",
    // gtaa-ga
    parkingLocation: "",
    noiseCertificate: ""
  }
}

export function getFlightModeText(flightMode: string) {
  switch (flightMode) {
    case "A":
      return flightModeText.A
    case "D":
      return flightModeText.D
    case "T":
      return flightModeText.T
    default:
      return flightMode
  }
}

export function getActionTypeText(actionType: ActionType) {
  return ActionTypeText[actionType] ?? actionType
}

export function getFlightForSubmit(flight: Flight): Flight {
  const updFlight = _.cloneDeep(flight)
  updFlight.minGroundTime = updFlight.minGroundTime ?? 0
  updFlight.woop = updFlight.woop ?? 1
  updFlight.turnaroundDays = updFlight.turnaroundDays ?? 0

  return updFlight
}

export enum ConstraintQuotaValue {
  QuotaPoints = "QuotaPoints",
  QuotaMovements = "QuotaMovements",
  QuotaSeats = "QuotaSeats"
}

export const constraintQuotaValues: string[] = [
  ConstraintQuotaValue.QuotaPoints,
  ConstraintQuotaValue.QuotaPoints,
  ConstraintQuotaValue.QuotaSeats
]
